import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Upload, Settings, BarChart3, Wrench, Copy, Check, CreditCard, DollarSign, Activity, Users as UsersIcon, Sparkles, FileText, X, Trash2, Power, Pause, Play, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import ActivityDashboard from '../components/ActivityDashboard';
import MasterSettingsModal from '../components/MasterSettingsModal';
import CheckerSpeedModal from '../components/CheckerSpeedModal';
import DailyCreditsModal from '../components/DailyCreditsModal';
import api from '../api/axios';
import Confetti from 'react-confetti';
import { parseAndCleanCards } from '../utils/cardUtils';
import { isAPILocked, getCardLimit, getSpeedOptions } from '../utils/planRestrictions';

const Home = () => {
  const { user, refreshUser, isPremium, isAdmin, isOwner } = useAuth();
  const notification = useNotification();
  const fileInputRef = useRef(null);
  const isRunningRef = useRef(false);
  const [isRunning, setIsRunning] = useState(false);
  const [cards, setCards] = useState([]);
  const [input, setInput] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('approved');
  const [selectedGateway, setSelectedGateway] = useState('STRIPE');
  const [selectedAPIIndex, setSelectedAPIIndex] = useState(0);
  const [showActivity, setShowActivity] = useState(false);
  const [showMasterSettings, setShowMasterSettings] = useState(false);
  const [showCheckerSpeed, setShowCheckerSpeed] = useState(false);
  const [showDailyCredits, setShowDailyCredits] = useState(false);
  const [selectedSpeed, setSelectedSpeed] = useState(localStorage.getItem('checkerSpeed') || 'normal');
  const [proxyEnabled, setProxyEnabled] = useState(localStorage.getItem('proxyEnabled') === 'true');
  const [soundEnabled, setSoundEnabled] = useState(localStorage.getItem('soundEnabled') !== 'false'); // Default true
  const [onlineCount, setOnlineCount] = useState(1);
  const [totalUsers, setTotalUsers] = useState(6612);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiConfig, setConfettiConfig] = useState({ width: 0, height: 0 });
  const [checkingProgress, setCheckingProgress] = useState(0);
  const [customAPIs, setCustomAPIs] = useState({ STRIPE: [], SHOPII: [] });
  
  // SK BASED options
  const [skKey, setSkKey] = useState('');
  const [chargeType, setChargeType] = useState('cvv');
  const [currency, setCurrency] = useState('myr');
  const [amount, setAmount] = useState('4');
  const [ppClientId, setPpClientId] = useState('');
  const [ppClientSecret, setPpClientSecret] = useState('');

  // Load saved settings from localStorage
  useEffect(() => {
    const savedGateway = localStorage.getItem('selectedGateway');
    if (savedGateway && ['STRIPE', 'SHOPII', 'PP', 'B3'].includes(savedGateway)) {
      setSelectedGateway(savedGateway);
    }
  }, []);

  // Sound effects
  const playApprovedSound = () => {
    if (!soundEnabled) return;
    try {
      const audio = new Audio();
      audio.volume = 0.4;
      audio.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  };

  const playLiveSound = () => {
    if (!soundEnabled) return;
    try {
      const audio = new Audio();
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  };

  useEffect(() => {
    // Update confetti size on window resize
    const updateSize = () => {
      setConfettiConfig({ 
        width: window.innerWidth, 
        height: window.innerHeight 
      });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Gateway APIs
  const gatewayAPIs = {
    STRIPE: [
      { id: 'st_auth_v1', name: 'Stripe Auth V1', price: 'A0.0$', endpoint: 'http://206.206.78.217:1011/', package: 'stv1', requiresPremium: false },
      { id: 'st_auth_v2', name: 'Stripe Auth V2', price: 'A0.0$', endpoint: 'http://206.206.78.217:1012/', package: 'stv2', requiresPremium: false },
      { id: 'st_auth_v3', name: 'Stripe Auth V3', price: 'A0.0$', endpoint: 'http://206.206.78.217:1013/', package: 'stv3', requiresPremium: false },
      { id: 'sk_based', name: 'SKBASED CVV', price: 'A1.0$', endpoint: 'skbased', package: 'skbased', requiresPremium: false },
    ],
    SHOPII: [
      { id: 'shopii_1', name: 'Shopii #1', price: 'A1.0$', endpoint: 'https://cyborxchecker.com/api/autog.php', package: 'shopii1', requiresPremium: true },
      { id: 'shopii_2', name: 'Shopii #2', price: 'A5.0$', endpoint: 'https://cyborxchecker.com/api/autog.php', package: 'shopii2', requiresPremium: false },
      { id: 'shopii_3', name: 'Shopii #3', price: 'A10.0$', endpoint: 'https://cyborxchecker.com/api/autog.php', package: 'shopii3', requiresPremium: false },
      { id: 'shopii_4', name: 'Shopii #4', price: 'A50.0$', endpoint: 'https://cyborxchecker.com/api/autog.php', package: 'shopii4', requiresPremium: false },
    ],
    PP: [
      { id: 'pp_keybased', name: 'PP KeyBased', price: 'A1.0$' },
      { id: 'pp_2', name: 'PP #2', price: 'A$$4.5' },
    ],
    B3: [
      { id: 'b3_1', name: 'B3 #1', price: 'A$$3.5' },
      { id: 'b3_2', name: 'B3 #2', price: 'A$$4.0' },
    ],
  };

  const currentAPIs = [...(gatewayAPIs[selectedGateway] || []), ...(customAPIs[selectedGateway] || [])];
  const currentAPI = currentAPIs[selectedAPIIndex];

  useEffect(() => {
    fetchStats();
    fetchCustomAPIs();
    // Show daily credits modal on page load
    setShowDailyCredits(true);
  }, []);

  // Auto-skip to first unlocked API when gateway changes or component loads
  useEffect(() => {
    if (currentAPIs.length === 0) return;
    
    const currentAPI = currentAPIs[selectedAPIIndex];
    if (currentAPI && isAPILocked(user?.plan, user?.status, currentAPI.name)) {
      // Current API is locked, find first unlocked one
      let foundIndex = -1;
      for (let i = 0; i < currentAPIs.length; i++) {
        if (!isAPILocked(user?.plan, user?.status, currentAPIs[i].name)) {
          foundIndex = i;
          break;
        }
      }
      
      if (foundIndex !== -1) {
        setSelectedAPIIndex(foundIndex);
      }
    }
  }, [selectedGateway, currentAPIs.length, user?.plan, user?.status]);

  // Handle credits claimed
  const handleCreditsClaimed = (newBalance) => {
    console.log('Credits claimed! New balance:', newBalance);
    // Refresh user data to update credits display
    refreshUser();
  };

  const fetchCustomAPIs = async () => {
    try {
      const response = await api.get('/user/auto-sites');
      if (response.data.success) {
        const packages = response.data.packages;
        const customStripe = [];
        const customShopii = [];

        packages.forEach(pkg => {
          const apiData = {
            id: `custom_${pkg.package_name}`,
            name: pkg.api_name,
            price: 'Custom',
            package: pkg.package_name,
            requiresPremium: false,
            isCustom: true
          };

          if (pkg.package_name.startsWith('stv')) {
            customStripe.push(apiData);
          } else if (pkg.package_name === 'shopii') {
            customShopii.push(apiData);
          }
        });

        setCustomAPIs({ STRIPE: customStripe, SHOPII: customShopii });
      }
    } catch (error) {
      console.error('Failed to fetch custom APIs');
    }
  };

  const fetchStats = async () => {
    try {
      const [onlineRes, statsRes] = await Promise.all([
        api.get('/user/online-users'),
        api.get('/user/stats')
      ]);
      if (onlineRes.data.success) setOnlineCount(onlineRes.data.count);
      if (statsRes.data.success) setTotalUsers(statsRes.data.stats.totalUsers || 6612);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const { validCards, errors } = parseAndCleanCards(text);
      
      if (validCards.length > 0) {
        setInput(validCards.join('\n'));
        if (errors.length > 0) {
          notification.warning(`Loaded ${validCards.length} valid cards. ${errors.length} cards were invalid or expired.`, 5000);
        } else {
          notification.success(`Loaded ${validCards.length} valid cards successfully!`, 3000);
        }
      } else {
        notification.error('No valid cards found in file!', 4000);
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset file input
  };

  const handleNextAPI = () => {
    let nextIndex = (selectedAPIIndex + 1) % currentAPIs.length;
    let attempts = 0;
    const maxAttempts = currentAPIs.length;
    
    // Keep skipping until we find an unlocked API
    while (attempts < maxAttempts) {
      const nextAPI = currentAPIs[nextIndex];
      
      // Check if API is locked for current user
      if (!isAPILocked(user?.plan, user?.status, nextAPI.name)) {
        setSelectedAPIIndex(nextIndex);
        return;
      }
      
      // Try next API
      nextIndex = (nextIndex + 1) % currentAPIs.length;
      attempts++;
    }
    
    // If all APIs are locked, show message
    notification.warning('All APIs are locked for your plan. Please upgrade!', 3000);
  };

  const handlePrevAPI = () => {
    let prevIndex = (selectedAPIIndex - 1 + currentAPIs.length) % currentAPIs.length;
    let attempts = 0;
    const maxAttempts = currentAPIs.length;
    
    // Keep skipping until we find an unlocked API
    while (attempts < maxAttempts) {
      const prevAPI = currentAPIs[prevIndex];
      
      // Check if API is locked for current user
      if (!isAPILocked(user?.plan, user?.status, prevAPI.name)) {
        setSelectedAPIIndex(prevIndex);
        return;
      }
      
      // Try previous API
      prevIndex = (prevIndex - 1 + currentAPIs.length) % currentAPIs.length;
      attempts++;
    }
    
    // If all APIs are locked, show message
    notification.warning('All APIs are locked for your plan. Please upgrade!', 3000);
  };

  const handleGatewayChange = (newGateway) => {
    setSelectedGateway(newGateway);
    setSelectedAPIIndex(0);
    localStorage.setItem('selectedGateway', newGateway);
  };

  const handleSpeedChange = (newSpeed) => {
    setSelectedSpeed(newSpeed);
    localStorage.setItem('checkerSpeed', newSpeed);
  };

  const handleProxyToggle = (enabled) => {
    setProxyEnabled(enabled);
    localStorage.setItem('proxyEnabled', enabled);
  };

  const handleSoundToggle = (enabled) => {
    setSoundEnabled(enabled);
    localStorage.setItem('soundEnabled', enabled);
  };

  const handleStart = async () => {
    if (!input.trim()) {
      notification.warning('Please paste or import cards', 3000);
      return;
    }

    // Check if current API is locked
    if (isAPILocked(user?.plan, user?.status, currentAPI?.name)) {
      notification.error(`${currentAPI?.name} is locked for your plan. Please upgrade to access this API.`, 5000);
      return;
    }

    // Check card limit
    const cardLimit = getCardLimit(user?.plan, user?.status);
    const parsedCards = parseAndCleanCards(input);
    if (parsedCards.length > cardLimit) {
      notification.error(`Your ${user?.plan || user?.status} plan allows checking up to ${cardLimit} cards at once. You have ${parsedCards.length} cards.`, 5000);
      return;
    }

    setIsRunning(true);
    isRunningRef.current = true; // Set ref immediately
    setCheckingProgress(0);

    try {
      const parseResponse = await api.post('/checker/parse', { text: input });
      if (!parseResponse.data.success || parseResponse.data.cards.length === 0) {
        notification.error('No valid cards found', 3000);
        setIsRunning(false);
        isRunningRef.current = false;
        return;
      }
      const cardsToCheck = parseResponse.data.cards.map(c => `${c.cardNumber}|${c.month}|${c.year}|${c.cvv}`);

      // Create session in localStorage instead of database
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const sessionData = {
        session_id: sessionId,
        gateway: selectedGateway,
        api_name: currentAPI?.name,
        speed: selectedSpeed,
        total_cards: 0,
        approved_cards: 0,
        live_cards: 0,
        dead_cards: 0,
        credits_used: 0,
        started_at: new Date().toISOString(),
        status: 'ACTIVE'
      };

      // Speed delays and concurrency (realistic for slow API)
      const speedConfig = {
        slow: { delay: 4000, concurrency: 1 },
        normal: { delay: 2000, concurrency: 2 },
        fast: { delay: 1000, concurrency: 3 },
        turbo: { delay: 500, concurrency: 5 }
      };
      
      const config = speedConfig[selectedSpeed] || speedConfig.normal;
      const { delay, concurrency } = config;

      console.log(`Starting check with ${concurrency} concurrent requests, ${delay}ms delay`);

      // Process cards in batches based on concurrency
      for (let i = 0; i < cardsToCheck.length; i += concurrency) {
        // Check ref for immediate stop
        if (!isRunningRef.current) {
          console.log('Checking stopped by user');
          break;
        }

        // Get batch of cards to check concurrently
        const batch = cardsToCheck.slice(i, i + concurrency);
        
        console.log(`Checking batch ${Math.floor(i / concurrency) + 1}: ${batch.length} cards concurrently`);
        
        // Check all cards in batch concurrently (with staggered start)
        const batchPromises = batch.map(async (cardStr, batchIndex) => {
          // Stagger concurrent requests to avoid overwhelming API
          const staggerDelay = batchIndex * 200; // 200ms between each request in batch
          if (staggerDelay > 0) {
            await new Promise(resolve => setTimeout(resolve, staggerDelay));
          }
          
          const [cardNumber, month, year, cvv] = cardStr.split('|');
          const overallIndex = i + batchIndex;
          
          try {
            console.log(`  Card ${overallIndex + 1}/${cardsToCheck.length}: ${cardNumber.substring(0, 6)}... (concurrent, +${staggerDelay}ms)`);
            
            // Determine endpoint based on gateway and API
            let checkEndpoint = '/stauth-checker/check';
            if (selectedGateway === 'SHOPII') {
              checkEndpoint = '/shopii-checker/check';
            } else if (currentAPI?.id === 'sk_based') {
              checkEndpoint = '/skbased-checker/check';
            } else if (currentAPI?.id === 'pp_keybased') {
              checkEndpoint = '/ppkeyavs-checker/check';
            }
            
            const requestBody = {
              cardNumber,
              month,
              year,
              cvv,
              gateway: selectedGateway,
              apiName: currentAPI?.name,
              speed: selectedSpeed,
              sessionId,
              useProxy: proxyEnabled
            };

            // Add SK BASED options for SK BASED checker
            if (currentAPI?.id === 'sk_based') {
              requestBody.sk = skKey || user?.sk_key || null; // User input, or user's saved key, or will use default from DB
              requestBody.charge_type = chargeType;
              requestBody.currency = currency;
              requestBody.amount = parseInt(amount);
            }

            // Add PayPal KeyBased options
            if (currentAPI?.id === 'pp_keybased') {
              requestBody.client_id = ppClientId || '';
              requestBody.client_secret = ppClientSecret || '';
            }

            const checkResponse = await api.post(checkEndpoint, requestBody);
            console.log(`  Card ${overallIndex + 1} result:`, checkResponse.data.result);

            // Handle both successful responses and error responses from API
            const result = {
              card: cardStr,
              result: checkResponse.data.result || 'DEAD',
              response: checkResponse.data.response || checkResponse.data.error || checkResponse.data.result,
              time: checkResponse.data.timeTaken || '0.00'
            };

            // Add result
            setCards(prev => [...prev, result]);

            // Update session data in sessionStorage
            sessionData.total_cards++;
            if (result.result === 'APPROVED') sessionData.approved_cards++;
            else if (result.result === 'LIVE') sessionData.live_cards++;
            else if (result.result === 'DEAD' || result.result === 'ERROR') sessionData.dead_cards++;
            sessionData.credits_used += checkResponse.data.creditDeducted || 0;

            if (result.result === 'APPROVED' || result.result === 'LIVE') {
                if (result.result === 'APPROVED') {
                  setShowConfetti(true);
                  playApprovedSound();
                  setTimeout(() => setShowConfetti(false), 5000); // 5 seconds for APPROVED
                } else if (result.result === 'LIVE') {
                  playLiveSound();
                  // No confetti for LIVE
                }
              }

              return result;
          } catch (error) {
            console.error(`  Card ${overallIndex + 1} failed:`, error.message);
            const errorResult = {
              card: cardStr,
              result: 'ERROR',
              response: error.response?.data?.error || 'Gateway timeout',
              time: '0.00'
            };
            setCards(prev => [...prev, errorResult]);
            return errorResult;
          }
        });

        // Wait for all cards in batch to complete
        await Promise.all(batchPromises);
        
        // Update progress
        const checkedCount = Math.min(i + concurrency, cardsToCheck.length);
        setCheckingProgress((checkedCount / cardsToCheck.length) * 100);
        
        // Remove checked cards from input
        const remaining = cardsToCheck.slice(checkedCount);
        setInput(remaining.join('\n'));
        
        // Refresh user credits after batch
        await refreshUser();

        // Apply speed delay between batches (but check if stopped)
        if (i + concurrency < cardsToCheck.length) {
          console.log(`Batch complete. Waiting ${delay}ms before next batch...`);
          for (let d = 0; d < delay; d += 100) {
            if (!isRunningRef.current) break;
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }

      // Don't save session to any storage - keep only in component state
      // Sessions will be lost on page reload (temporary data)
      setInput('');
    } catch (error) {
      notification.error(error.response?.data?.error || 'Checking failed', 4000);
    } finally {
      setIsRunning(false);
      isRunningRef.current = false;
    }
  };

  const handleStop = () => {
    isRunningRef.current = false; // Set ref immediately for instant stop
    setIsRunning(false);
    console.log('Stop button clicked - halting checks');
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const approvedCards = cards.filter(c => c.result === 'APPROVED');
  const liveCards = cards.filter(c => c.result === 'LIVE');
  const deadCards = cards.filter(c => c.result === 'DEAD' || c.result === 'ERROR'); // Include ERROR in DEAD tab

  return (
    <>
      {showConfetti && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999, overflow: 'hidden' }}>
          <Confetti 
            width={confettiConfig.width || window.innerWidth} 
            height={confettiConfig.height || window.innerHeight}
            recycle={false} 
            numberOfPieces={300}
            gravity={0.3}
          />
        </div>
      )}
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#0a1628] pb-24 relative overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div key={i} className="absolute w-1 h-1 bg-cyan-400 rounded-full" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }} animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }} transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 3 }} />
        ))}

        <div className="max-w-md mx-auto px-4 py-6">
          <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-2">
              <motion.div className="flex items-center gap-2" initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
                <motion.div className="bg-purple-500/30 rounded-full pl-2 pr-3 py-1 border border-purple-500/50 flex items-center gap-1.5" whileHover={{ scale: 1.05 }}>
                  <CreditCard size={12} className="text-purple-400 sm:w-3.5 sm:h-3.5" />
                  <span className="text-xs sm:text-sm font-medium text-white">{user?.credit || 97}</span>
                </motion.div>
                <motion.div className="bg-green-500/30 rounded-full pl-2 pr-3 py-1 border border-green-500/50 flex items-center gap-1.5" whileHover={{ scale: 1.05 }}>
                  <DollarSign size={12} className="text-green-400 sm:w-3.5 sm:h-3.5" />
                  <span className="text-xs sm:text-sm font-medium text-white">{(user?.cash || 0).toFixed(2)}</span>
                </motion.div>
              </motion.div>
              <motion.div className="flex items-center gap-2" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
                <motion.div className="bg-green-500/30 rounded-full pl-2 pr-3 py-1 border border-green-500/50 flex items-center gap-1.5" whileHover={{ scale: 1.05 }}>
                  <Activity size={12} className="text-green-400 sm:w-3.5 sm:h-3.5" />
                  <span className="text-xs sm:text-sm font-medium text-white">{onlineCount}</span>
                </motion.div>
                <motion.div className="bg-blue-500/30 rounded-full pl-2 pr-3 py-1 border border-blue-500/50 flex items-center gap-1.5" whileHover={{ scale: 1.05 }}>
                  <UsersIcon size={12} className="text-blue-400 sm:w-3.5 sm:h-3.5" />
                  <span className="text-xs sm:text-sm font-medium text-white">{totalUsers}</span>
                </motion.div>
              </motion.div>
            </div>
            <motion.div className="relative inline-block w-full">
              <motion.h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-1 text-center tracking-tight relative z-10" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3, duration: 0.5, type: 'spring' }}>CyborX Webapp</motion.h1>
              {[...Array(8)].map((_, i) => (
                <motion.div key={i} className="absolute" style={{ left: `${40 + Math.random() * 20}%`, top: `${20 + Math.random() * 60}%` }} animate={{ y: [0, -30, 0], opacity: [0, 1, 0], scale: [0, 1, 0] }} transition={{ duration: 2 + Math.random(), repeat: Infinity, delay: i * 0.3 }}>
                  <Sparkles size={12 + Math.random() * 8} className="text-cyan-400" />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-6">
            <motion.div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-5 shadow-2xl relative overflow-visible border border-gray-700/50">
              <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ filter: 'blur(2px)' }}>
                <defs><linearGradient id="borderGradient" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="transparent" /><stop offset="50%" stopColor="rgba(6, 182, 212, 0.8)" /><stop offset="100%" stopColor="transparent" /></linearGradient></defs>
                <motion.rect x="0" y="0" width="100%" height="100%" rx="16" fill="none" stroke="url(#borderGradient)" strokeWidth="3" strokeDasharray="1000" initial={{ strokeDashoffset: 0 }} animate={{ strokeDashoffset: [0, -2000] }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }} />
              </svg>
              <div className="absolute inset-0 rounded-2xl border-2 border-cyan-500/20 pointer-events-none"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-2xl pointer-events-none"></div>

              <textarea value={input} onChange={(e) => setInput(e.target.value)} placeholder="Paste your cards here or import file..." className="w-full bg-transparent text-white outline-none resize-none font-mono text-xs sm:text-sm relative z-10 placeholder-gray-500" rows={4} disabled={isRunning} />

              <div className="flex items-center justify-between mt-4 relative z-10">
                <div className="flex gap-2">
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".txt" className="hidden" />
                  <motion.button onClick={() => fileInputRef.current?.click()} className="p-2.5 hover:bg-gray-700/50 rounded-xl transition-all group" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} title="Import File">
                    <FileText size={20} className="text-gray-400 group-hover:text-cyan-400 transition-colors" />
                  </motion.button>
                </div>
                <div className="flex gap-2">
                  <motion.button onClick={() => setShowActivity(true)} className="p-2.5 hover:bg-gray-700/50 rounded-xl transition-all group" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                    <BarChart3 size={20} className="text-gray-400 group-hover:text-cyan-400 transition-colors" />
                  </motion.button>
                  <motion.button onClick={() => setShowCheckerSpeed(true)} className="p-2.5 hover:bg-gray-700/50 rounded-xl transition-all group" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                    <Wrench size={20} className="text-gray-400 group-hover:text-cyan-400 transition-colors" />
                  </motion.button>
                  <motion.button onClick={() => setShowMasterSettings(true)} className="p-2.5 hover:bg-gray-700/50 rounded-xl transition-all group" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                    <Settings size={20} className="text-gray-400 group-hover:text-cyan-400 transition-colors" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mb-6">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4 sm:mb-5">
              <motion.button className="p-2 sm:p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-xl border border-gray-700/50" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={handlePrevAPI}>
                <ChevronLeft size={20} className="text-gray-400 sm:w-6 sm:h-6" />
              </motion.button>
              <motion.div className="flex items-center gap-2 sm:gap-3 bg-gradient-to-br from-gray-800/60 to-gray-900/60 px-4 sm:px-6 py-2 sm:py-3 rounded-xl border border-gray-700/50 shadow-lg" whileHover={{ scale: 1.05 }}>
                <div className={`w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br ${isAPILocked(user?.plan, user?.status, currentAPI?.name) ? 'from-gray-500 to-gray-600' : 'from-cyan-500 to-blue-600'} rounded-lg flex items-center justify-center shadow-lg ${isAPILocked(user?.plan, user?.status, currentAPI?.name) ? 'shadow-gray-500/30' : 'shadow-cyan-500/30'}`}>
                  {isAPILocked(user?.plan, user?.status, currentAPI?.name) ? (
                    <Lock size={14} className="text-white sm:w-[18px] sm:h-[18px]" strokeWidth={2.5} />
                  ) : (
                    <CreditCard size={14} className="text-white sm:w-[18px] sm:h-[18px]" strokeWidth={2.5} />
                  )}
                </div>
                <div>
                  <span className={`font-bold text-sm sm:text-lg ${isAPILocked(user?.plan, user?.status, currentAPI?.name) ? 'text-gray-400' : 'text-white'}`}>
                    {currentAPI?.name}
                    {isAPILocked(user?.plan, user?.status, currentAPI?.name) && (
                      <span className="ml-2 text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Locked</span>
                    )}
                  </span>
                  <span className="text-green-400 text-xs sm:text-sm font-bold ml-2 sm:ml-3">{currentAPI?.price}</span>
                </div>
              </motion.div>
              <motion.button className="p-2 sm:p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-xl border border-gray-700/50" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={handleNextAPI}>
                <ChevronRight size={20} className="text-gray-400 sm:w-6 sm:h-6" />
              </motion.button>
            </div>


            <div className="flex gap-2 sm:gap-3">
              <motion.button onClick={handleStart} disabled={isRunning || !input.trim()} className={`flex-1 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all shadow-lg border ${isRunning || !input.trim() ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed border-gray-700/50' : 'bg-gray-700/50 text-white border-gray-600 hover:bg-gray-600/50'}`} whileHover={!isRunning && input.trim() ? { scale: 1.02 } : {}} whileTap={!isRunning && input.trim() ? { scale: 0.98 } : {}}>
                Start
              </motion.button>
              <motion.button onClick={handleStop} disabled={!isRunning} className={`flex-1 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all shadow-lg border ${!isRunning ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed border-gray-700/50' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white border-red-600'}`} whileHover={isRunning ? { scale: 1.02 } : {}} whileTap={isRunning ? { scale: 0.98 } : {}}>
                Stop
              </motion.button>
            </div>
            {checkingProgress > 0 && checkingProgress < 100 && (
              <div className="mt-3">
                <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${checkingProgress}%` }} />
                </div>
                <p className="text-xs text-gray-500 text-center mt-1">{checkingProgress.toFixed(0)}% Complete</p>
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mb-6">
            <div className="flex gap-2">
              <motion.button onClick={() => setActiveTab('approved')} className={`flex-1 py-3 sm:py-4 font-bold rounded-xl shadow-lg text-xs sm:text-sm ${activeTab === 'approved' ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white border-2 border-orange-500' : 'bg-gray-800/50 text-gray-400 border-2 border-gray-700/50'}`} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>APPROVED ({approvedCards.length})</motion.button>
              <motion.button onClick={() => setActiveTab('live')} className={`flex-1 py-3 sm:py-4 font-bold rounded-xl shadow-lg text-xs sm:text-sm ${activeTab === 'live' ? 'bg-gradient-to-r from-green-600 to-green-700 text-white border-2 border-green-500' : 'bg-gray-800/50 text-gray-400 border-2 border-gray-700/50'}`} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>LIVE ({liveCards.length})</motion.button>
              <motion.button onClick={() => setActiveTab('dead')} className={`flex-1 py-3 sm:py-4 font-bold rounded-xl shadow-lg text-xs sm:text-sm ${activeTab === 'dead' ? 'bg-gradient-to-r from-red-600 to-red-700 text-white border-2 border-red-500' : 'bg-gray-800/50 text-gray-400 border-2 border-gray-700/50'}`} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>DEAD ({deadCards.length})</motion.button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50 max-h-64 overflow-y-auto shadow-2xl">
            <AnimatePresence mode="wait">
              {(activeTab === 'approved' ? approvedCards : activeTab === 'live' ? liveCards : deadCards).length > 0 ? (
                <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-3">
                  {(activeTab === 'approved' ? approvedCards : activeTab === 'live' ? liveCards : deadCards).map((cardObj, idx) => (
                    <motion.div key={idx} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }} className={`flex items-center justify-between p-4 rounded-xl border ${activeTab === 'approved' ? 'bg-orange-900/20 border-orange-500/30 hover:border-orange-500/50' : activeTab === 'live' ? 'bg-green-900/20 border-green-500/30 hover:border-green-500/50' : 'bg-red-900/20 border-red-500/30 hover:border-red-500/50'}`} whileHover={{ scale: 1.02 }}>
                      <div className="flex items-center gap-3 flex-1">
                        <motion.div className={`w-2.5 h-2.5 rounded-full ${activeTab === 'approved' ? 'bg-orange-500' : activeTab === 'live' ? 'bg-green-500' : 'bg-red-500'}`} animate={activeTab === 'approved' || activeTab === 'live' ? { scale: [1, 1.3, 1] } : {}} transition={{ repeat: Infinity, duration: 2 }} />
                        <div className="flex-1">
                          <span className={`font-mono text-sm block ${activeTab === 'approved' ? 'text-orange-300' : activeTab === 'live' ? 'text-green-300' : 'text-red-300'}`}>{cardObj.card}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-400">{cardObj.response}</span>
                            {cardObj.time && <span className="text-xs text-gray-500">• {cardObj.time}s</span>}
                          </div>
                        </div>
                      </div>
                      <motion.button onClick={() => handleCopy(cardObj.card, idx)} className={`p-2 rounded-lg ${activeTab === 'approved' ? 'hover:bg-orange-700/30' : activeTab === 'live' ? 'hover:bg-green-700/30' : 'hover:bg-red-700/30'}`} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                        {copiedIndex === idx ? <Check size={16} className="text-white" /> : <Copy size={16} className="text-gray-400" />}
                      </motion.button>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
                  <CreditCard size={48} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 text-sm">No {activeTab} cards yet</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      <ActivityDashboard isOpen={showActivity} onClose={() => setShowActivity(false)} approvedCount={approvedCards.length} liveCount={liveCards.length} deadCount={deadCards.length} totalCount={cards.length} />
      <MasterSettingsModal isOpen={showMasterSettings} onClose={() => setShowMasterSettings(false)} selectedGateway={selectedGateway} onGatewayChange={handleGatewayChange} />
      <CheckerSpeedModal isOpen={showCheckerSpeed} onClose={() => setShowCheckerSpeed(false)} selectedSpeed={selectedSpeed} setSelectedSpeed={handleSpeedChange} userPlan={user?.plan} userStatus={user?.status} proxyEnabled={proxyEnabled} setProxyEnabled={handleProxyToggle} soundEnabled={soundEnabled} setSoundEnabled={handleSoundToggle} />
      <DailyCreditsModal isOpen={showDailyCredits} onClose={() => setShowDailyCredits(false)} onCreditsClaimed={handleCreditsClaimed} />
    </>
  );
};


export default Home;
